import { inject, Inject, Injectable } from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ConfirmCloseDialogComponent } from '@components/dialog/children/confirm-close-dialog/confirm-close-dialog.component';
import { IOutputDataConfirmCloseDialog } from '@components/dialog/interfaces/dialog.interface';
import { LANG_DICTIONARY_TOKEN } from '@constants/diConstant';
import { IMainLang } from '@interfaces/languages/main-lang';
import { Observable } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { ConfirmDialogComponent } from '../children/confirm-dialog/confirm-dialog.component';
import { DialogComponent } from '../component/dialog.component';

/**
 * Сервис для диалога подтверждения действия
 *
 * @package Dialog
 * @version 2.0
 * @author Bolotov I. P.
 * @since 17.03.2023
 *
 */
@Injectable({
    providedIn: 'root',
})
export class AppDialogService {
    readonly #media: MediaObserver = inject(MediaObserver);

    /** Ширина модального окна по умолчанию */
    private DEFAULT_WIDTH = 30;
    private DIALOG_CLOSE_WIDTH = '45vw';

    constructor(
        private readonly _dialog: MatDialog,
        @Inject(LANG_DICTIONARY_TOKEN) private readonly _dictionary: IMainLang = null,
    ) {}

    /**
     * Диалог подтверждения действия
     * @param title - заголовок
     * @param message - сообщение
     */
    public confirm(
        title: string = this._dictionary.system_confirm_action,
        message: string = this._dictionary.system_confirm_are_you_sure,
    ): Observable<boolean> {
        const dialog = DialogComponent.commonDialog(this._dialog, ConfirmDialogComponent, {
            width: this.getDialogWidth(),
            autoFocus: false,
            data: {
                title,
                message,
            },
        });

        this.#media
            .asObservable()
            .pipe(takeUntil(dialog.afterClosed()))
            .subscribe(() => {
                dialog.updateSize(this.getDialogWidth(1));
            });

        return dialog.afterClosed().pipe(
            map((res: boolean | { save?: boolean }) => {
                switch (typeof res) {
                    case 'object':
                        return res?.save === true;
                    case 'boolean':
                        return res === true;
                }
            }),
        );
    }

    /**
     * Диалог подтверждения закрытия
     * @param title - заголовок
     * @param message - сообщение
     */
    public confirmClose(
        title: string = this._dictionary.system_confirm_action,
        message: string = this._dictionary.system_confirm_are_you_sure,
    ): Observable<IOutputDataConfirmCloseDialog> {
        const dialog = DialogComponent.commonDialog(this._dialog, ConfirmCloseDialogComponent, {
            width: this.getDialogWidth(1.5),
            autoFocus: false,
            data: {
                title,
                message,
            },
        });

        this.#media
            .asObservable()
            .pipe(takeUntil(dialog.afterClosed()))
            .subscribe(() => {
                dialog.updateSize(this.getDialogWidth(1.5));
            });

        return dialog.afterClosed().pipe(
            filter((result: IOutputDataConfirmCloseDialog) => {
                return result.close || result.save;
            }),
        );
    }

    private getDialogWidth(coef: number = 1): string {
        let width = this.DEFAULT_WIDTH;
        if (this.#media.isActive('lt-md')) {
            width = this.DEFAULT_WIDTH * 1.7;
        }
        if (this.#media.isActive('lt-sm')) {
            width = this.DEFAULT_WIDTH * 3;
        }
        return width * coef + 'vw';
    }
}
